// ═══════════════════════════════════════════════════════════════════════════
// consolidarPorUserProduct — candidatas a Full consolidadas por MLAU (L2VplkwI)
// ───────────────────────────────────────────────────────────────────────────
// Pedido Mateo (6-jul-2026): en el tablero de Full conviene consolidar por
// `user_product_id` (MLAU…) cuando existe, porque con el modelo de user
// products el STOCK vive a nivel producto-del-seller y N publicaciones (MLA)
// pueden compartirlo — contarlo por MLA lo duplica. Ítems SIN MLAU quedan
// como fila propia por MLA (no todos migraron al modelo todavía).
//
// Reglas de agregación (declaradas, mismas semánticas que las filas por MLA):
//   - unidades / facturación / órdenes 30d → SUMA del grupo (venta real total).
//   - stock local → MÁXIMO del grupo (stock COMPARTIDO: sumar duplicaría; si
//     las publicaciones reportan valores distintos se toma el mayor y se
//     declara el criterio en el tooltip de la UI).
//   - velocidad = unidades / ventanaDias · sugerido = cobertura de
//     DIAS_METAS_FULL días a esa velocidad (`ceil(unidades × 30 / ventana)`)
//     · gap = max(0, sugerido − stock) · cobertura = stock ≥ sugerido.
//     `ventanaDias` es OBLIGATORIO (report djynjlXICuUF0Yl803M8): es la ventana
//     con la que el llamador cargó las ventas, y sin ella velocidad, sugerido,
//     gap y cobertura salen `null` — nunca un 30 inventado.
//   - fila representante (título/precio/thumbnail/permalink/tamaño): la de
//     MÁS unidades vendidas del grupo (la publicación que tracciona).
//   - `_categoriaYaEnFull` → OR del grupo. `_mlas` lista TODOS los MLA y
//     `_skus` los SKU distintos (para el CSV — pedido NI8YfVFV).
//
// PURA y testeable — sin React, sin Firestore.
// ═══════════════════════════════════════════════════════════════════════════

import { ventanaDeVentas, escalarADias } from '@core/lib/clasificadores/ventanaDeVentas'

/**
 * Las metas de Full se fijan en días por PRODUCTO, no por la ventana de
 * ventas: el envío sugerido cubre 30 días y la rotación baja es «menos de 3
 * u/30d». Con la ventana en 30 da idéntico; con otra, las unidades se
 * normalizan a estos días en vez de compararse crudas (con 60 días crudos se
 * mandaría el doble a Full y se escondería la rotación baja).
 */
export const DIAS_METAS_FULL = 30

/** Las unidades de una ventana de `ventanaDias`, llevadas a DIAS_METAS_FULL días; `null` sin ventana. */
export function unidadesEnDiasMeta(unidades, ventanaDias) {
  return escalarADias(unidades, ventanaDias, DIAS_METAS_FULL)
}

/**
 * @param {Array<object>} rows — filas candidatas por MLA (ya filtradas:
 *   activas, no-Full, con ventas), con `_unidades30d`, `_facturacion30d`,
 *   `_ordenes30d`, `_stockLocal`, `userProductId`, `sellerSku`, etc.
 * @param {{ventanaDias: number|null}} opts — la ventana con la que se cargaron las ventas
 * @returns {Array<object>} una fila por MLAU (o por MLA cuando no hay MLAU).
 */
export function consolidarPorUserProduct(rows, { ventanaDias } = {}) {
  const ventana = ventanaDeVentas(ventanaDias)
  const grupos = new Map()
  const sueltas = []
  for (const r of rows || []) {
    const upId = r?.userProductId
    if (upId == null || String(upId) === '') {
      sueltas.push(r)
      continue
    }
    const k = String(upId)
    if (!grupos.has(k)) grupos.set(k, [])
    grupos.get(k).push(r)
  }

  const out = []
  for (const r of sueltas) {
    out.push({
      ...r,
      _consolidadoPor: 'mla',
      _mlas: [String(r.itemId ?? r.id)],
      _skus: r.sellerSku ? [String(r.sellerSku)] : [],
    })
  }
  for (const [upId, gr] of grupos.entries()) {
    if (gr.length === 1) {
      out.push({
        ...gr[0],
        _consolidadoPor: 'mlau',
        _mlas: [String(gr[0].itemId ?? gr[0].id)],
        _skus: gr[0].sellerSku ? [String(gr[0].sellerSku)] : [],
      })
      continue
    }
    // Representante: la publicación con más unidades (desempate: más facturación).
    const rep = gr.slice().sort((a, b) =>
      (b._unidades30d - a._unidades30d) || (b._facturacion30d - a._facturacion30d)
    )[0]
    const unidades = gr.reduce((s, r) => s + (Number(r._unidades30d) || 0), 0)
    const facturacion = gr.reduce((s, r) => s + (Number(r._facturacion30d) || 0), 0)
    const ordenes = gr.reduce((s, r) => s + (Number(r._ordenes30d) || 0), 0)
    const stockLocal = Math.max(...gr.map((r) => Number(r._stockLocal) || 0))
    const u30 = unidadesEnDiasMeta(unidades, ventana)
    const sugerido = u30 === null ? null : Math.ceil(u30)
    const skus = [...new Set(gr.map((r) => r.sellerSku).filter(Boolean).map(String))]
    out.push({
      ...rep,
      // id estable para la key de la tabla (no pisa itemId del representante).
      id: String(upId),
      _consolidadoPor: 'mlau',
      _mlas: gr.map((r) => String(r.itemId ?? r.id)),
      _skus: skus,
      _unidades30d: unidades,
      _facturacion30d: facturacion,
      _ordenes30d: ordenes,
      _stockLocal: stockLocal,
      _sugeridoFull: sugerido,
      _gap: sugerido === null ? null : Math.max(0, sugerido - stockLocal),
      _velocidad: ventana === null ? null : unidades / ventana,
      _cobertura: sugerido === null ? null : stockLocal >= sugerido,
      _categoriaYaEnFull: gr.some((r) => !!r._categoriaYaEnFull),
    })
  }
  out.sort((a, b) => b._sugeridoFull - a._sugeridoFull)
  return out
}

/**
 * Coeficiente órdenes/unidades (NI8YfVFV): cerca de 1 = cada unidad salió en
 * una orden distinta (demanda repartida); cerca de 0 = pocas órdenes se
 * llevaron muchas unidades (outlier mayorista → proyección inflada, riesgo
 * de sobrestock en Full). null si no hay unidades.
 */
export function coefOrdenesUnidades(ordenes, unidades) {
  const u = Number(unidades) || 0
  if (u <= 0) return null
  return (Number(ordenes) || 0) / u
}

/** Advertencia de rotación baja (NI8YfVFV): menos de `umbral` unidades cada DIAS_METAS_FULL días.
 *  Recibe unidades YA normalizadas (`unidadesEnDiasMeta`), no las crudas de la ventana. */
export const UMBRAL_ROTACION_BAJA = 3
export function esRotacionBaja(unidades30d, umbral = UMBRAL_ROTACION_BAJA) {
  return (Number(unidades30d) || 0) < umbral
}
